import React, { useContext } from 'react';
import { Product, patchProduct } from '../../../../model/product';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { Label } from '../../../../components/Form/Label/Label';
import { Button } from '../../../../components/Button/Button';
import { Datepicker } from '../../../../components/Form/Datepicker/Datepicker';
import { Form, useChanges } from '../../../../components/Form/Form';
import { handleError } from '../../../../model/notification';
import { AsyncSearch } from '../../../../components/Form/AsyncSearch/AsyncSearch';
import { useRemoteData } from '../../../../model/data';
import { t } from '../../../../locales/translate';
import { getContract, vatRateMatchingContractVatRate } from '../../../../model/contract';
import { useErrorHandler } from '../../../../util/util';
import { ActivitiesContext } from '../../../../components/Activities/Activities';
import { Stack } from '../../../../components/Stack/Stack';
import { Spacer } from '../../../../components/Spacer/Spacer';
import { Switcher } from '../../../../components/Form/Switcher/Switcher';
import { SLUG_REGEX, regexToPattern } from '../../../../model/regex';
import { Select } from '../../../../components/Form/Select/Select';

interface Props {
  controller: ModalController<Product>;
}

export function ProductForm({ controller }: Props): JSX.Element {
  const { reloadActivities } = useContext(ActivitiesContext);
  const [changes, setChanges, product, trackChanges] = useChanges<Product>({
    ...controller.model,
    vendorIds: controller.model.vendors?.map((item) => item.id) || [],
    owningCompanyId: controller.model.owningCompany?.id,
  });
  const googleFeedCategoryOptions = product.googleFeedCategoryOptions.map((category) => ({
    value: category,
    label: t.model.product.googleFeedCategories[category],
  }));

  async function handleSubmit(): Promise<void> {
    try {
      if (Object.keys(changes).length > 0) {
        await patchProduct(product.id, changes);
        reloadActivities();
      }
      controller.close();
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <div className="product-general-info-card">
      <Spacer size="lg" />
      <Form model={product} onInput={trackChanges}>
        {(form) => {
          controller.configure({
            onSubmit: handleSubmit,
            canSubmit: form.valid,
          });
          return (
            <Stack>
              <Label title={t.model.product.general} />
              <TextField
                label={t.model.product.slug}
                name="slug"
                pattern={regexToPattern(SLUG_REGEX)}
                description={t.model.product.slugFormat}
              />
              <Spacer size="none" />
              <Label title={t.model.company.vendorInformation} />
              <div className="product-general-info-card__wrapper2">
                <AsyncSearch label={t.model.company.vendors} name="vendorIds" searchIn={['company']} />
              </div>
              <Spacer size="none" />
              <Label title={t.model.product.contractItemOwner} />
              <AsyncSearch label={t.model.event.owner} name="owningCompanyId" searchIn={['company']} />
              <ContractField contractId={product.contractId || ''} deTaxRate={product.taxesPerCountry.de} />
              <div className="product-general-info-card__datepicker">
                <Datepicker name="publishedFrom" hourpicker label={t.model.datetime.publishDateTime} required />
                <Button
                  onClick={() => {
                    setChanges({
                      ...changes,
                      publishedFrom: new Date(),
                    });
                  }}
                  title={t.general.now}
                />
              </div>
              <Datepicker name="shippableFrom" hourpicker label={t.model.product.shippableFrom} />
              <Datepicker name="dhlShippingFrom" hourpicker label={t.model.product.dhlShippableFrom} />
              <Datepicker
                name="publicShippableFrom"
                label={t.model.product.publicShippableFrom}
                disabledDays={[{ before: new Date() }]}
              />
              <TextField name="costAccountOne" label={`${t.model.product.costAccount} 1`} />
              <TextField name="costAccountTwo" label={`${t.model.product.costAccount} 2`} />
              <TextField name="customsTariffNumber" label={t.model.product.customsTariffNumber} />
              <Switcher
                value={product.excludeFromFeeds}
                label={t.model.product.excludeFromFeeds}
                labels={[t.general.yes, t.general.no]}
                onChange={(value) => setChanges({ ...changes, excludeFromFeeds: value })}
              />
              <Select
                label={t.model.product.googleFeedCategory}
                name="googleFeedCategory"
                options={googleFeedCategoryOptions}
                allowEmpty
              />
              <TextField name="legacyId" label={t.general.legacyId} />
            </Stack>
          );
        }}
      </Form>
    </div>
  );
}

function ContractField({ contractId, deTaxRate }: { contractId: string; deTaxRate?: number }): JSX.Element | null {
  const [selectedContract, loading, error] = useRemoteData(
    () => getContract(contractId),
    (db) => {
      return db.table('contracts').find(contractId);
    },
    [contractId]
  );
  useErrorHandler(error);

  const taxMatchesContractRate = vatRateMatchingContractVatRate(selectedContract, deTaxRate);

  return (
    <AsyncSearch
      label={t.model.contract._name}
      name="contractId"
      searchIn={['contract']}
      errorOverride={contractId && deTaxRate && !taxMatchesContractRate ? t.model.price.vatRateMismatch : undefined}
      isLoading={loading}
    />
  );
}
